import isEmpty from 'lodash/isEmpty';
import { MdOutlineFlip } from 'react-icons/md';
import React, { useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useIntl } from 'react-intl';

import styled from 'styled-components/macro';
import { uiColors } from '../../common/styles';
import FramiIconMenu from '../common/FramiIconMenu';
import { useAppContext, useCompositionTemplates, useFrami, useNavigation } from '../../context';
import { flipX, flipXY, flipY } from '../../providers/CompositionTemplateProvider';
import { FRAME_COUNT_FILTER, PRICE_FILTER, WALLSIZE_DRAWER } from '../../providers/NavigationProvider';
import ToolbarAnimatedHint from './ToolbarAnimatedHint';
import { FlipVertical, RotateRight } from './styled';
import { calculateWallHeight, calculateWallWidth } from '../../utilities/helpers';
import { ButtonGroup } from './OptionButton';
import AccordionButton from '../common/AccordionButton';
import FramiMenu from '../common/FramiMenu';
import FramiIconButton from '../common/FramiIconButton';
import { FilterIcon } from '../../icons';
import { FlexSpacer } from './OptionButton';
import { MdFilterNone } from 'react-icons/md';
import { contentPaddingSides } from '../../common/styles';
import ScrollHorizontalContainer from '../common/ScrollHorizontalContainer';


const CategoryIcon = styled.div`
    fill: currentColor;
    transform: scale(1.25);
    height: 20px;
    width: 20px;
    display: inherit;
`;

const Nowrap = styled.span`
    white-space: nowrap;
    display: inline-flex;
    margin: 0px 0px !important;
`;

const CategoryIconSelected = styled.span`
    fill: ${uiColors.PRIMARY_TEXT}
    transform: scale(1.25);
    height: 22px;
    width: 22px;
    display: inherit;
`;

const FilterWrapper = styled.section`
    // box-shadow: ${(props) => (props.$showFilters ? '0 8px 7px -6px rgba(0, 0, 0, 0.1)' : '')};
    display: flex;
    flex-direction: column;
    position: relative;
    top: ${(props) => (props.$showFilters ? 0 : '-80px')};
    gap: 4px;
    overflow-x: ${(props) => (props.$isMobile ? 'auto' : 'unset')};
    overflow-y: ${(props) => (props.$isMobile ? 'hidden' : 'unset')};
    background-color: white;
    transition: all 0.4s ease-in-out;
    z-index: 1;
    // flex-wrap: wrap;
    // min-height: ${(props) => (props.$showFilters ? '80px' : '0')};
    padding: 8px 0px;
    
    & > div {  // targeting ButtonGroup
        // flex-wrap: wrap;
    }

    & > div > div {  // targeting ScrollHorizontalContainer > ButtonGroup
        height: 34px;
        min-height: 34px;
    }

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${uiColors.ACTIVE_BORDER};
        border-radius: 5px;
    }

    &::-webkit-scrollbar-track {
        background-color: ${uiColors.GRAY_100};
    }
`;

const RelativeWrapper = styled.div`
    position: relative;
`;

const AdditionalFilters = ({ showFilters }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const { isMobile, smallHeightDevice } = useAppContext();
    const { currentFrami, memoizedCategories } = useFrami();
    const { sortOptions, filterFrameCounts, filterPriceCurrency, filterPriceApplied, flipComposition, isCategoryStairs, setFlipComposition, setFilterFrameCounts, setFilterPriceApplied, filterCategoryId, setFilterCategoryId, filterCategoryAvailableCounts, setSortingCriterion, sortingCriterion } =
        useCompositionTemplates();
    const { activeHint, openDrawer } = useNavigation();

    const [cookies, setCookie] = useCookies(['templateSizeClickHintSeen']);
    const intl = useIntl();

    const notEmptyCount = !isEmpty(filterFrameCounts);

    const frameFilterButtonTitle = intl.formatMessage(
        { id: 'studio.drawer.templates.filter.frames' },
        {
            colon: notEmptyCount ? ':' : '',
            counts: notEmptyCount ? `${filterFrameCounts}` : '',
            strong: (...chunks) => <strong>{chunks}</strong>,
        }
    );

    const handleOpenMenu = (event, func) => {
        func(event);
        setIsMenuOpen(true);
    };

    const handleCloseMenu = () => {
        setIsMenuOpen(false);
    };

    const flipOptions = useMemo(() => {
        const options = [
            {
                text: intl.formatMessage({ id: 'studio.drawer.templates.filter.flip.default' }),
                onClick: () => setFlipComposition(false),
                selected: flipComposition === false,
            },
            {
                text: intl.formatMessage({ id: 'studio.drawer.templates.filter.flip.vertical' }),
                icon: <FlipVertical />,
                onClick: () => setFlipComposition(flipY),
                selected: flipComposition === flipY,
            },
            {
                text: intl.formatMessage({ id: 'studio.drawer.templates.filter.flip.horizontal' }),
                icon: <MdOutlineFlip size={20} />,
                onClick: () => setFlipComposition(flipX),
                selected: flipComposition === flipX,
            },
            {
                text: intl.formatMessage({ id: 'studio.drawer.templates.filter.flip.180' }),
                icon: <RotateRight />,
                onClick: () => setFlipComposition(flipXY),
                selected: flipComposition === flipXY,
            },
        ];
        return options;
    }, [intl, flipComposition, setFlipComposition]);

    const flipOptionsCategories = useMemo(() => {
        if (!memoizedCategories || !filterCategoryAvailableCounts) return [];
        
        const options = [
            {
                text: intl.formatMessage({ id: 'studio.composition.type.all' }),
                onClick: () => setFilterCategoryId(null),
                selected: !filterCategoryId,
                icon: <MdFilterNone style={{ display: 'inline' }} transform="scale(1.25)" />
            },
            ...Array.from(memoizedCategories.values())
                .filter(ct => ct.svg_icon && !ct.is_archive)
                .map(ct => ({
                    text: ct.name,
                    icon: <CategoryIcon dangerouslySetInnerHTML={{ __html: ct.svg_icon }} />,
                    onClick: () => setFilterCategoryId(ct.id),
                    selected: ct.id === filterCategoryId,
                    disabled: (filterCategoryAvailableCounts[ct.id] || 0) === 0
                }))
        ];
        return options;
    }, [memoizedCategories, filterCategoryId, filterCategoryAvailableCounts, intl]);

    const getCurrentCategoryIcon = useMemo(() => {
        if (!filterCategoryId || !memoizedCategories) return null;
        const currentCategory = memoizedCategories.get(filterCategoryId);
        if (!currentCategory || !currentCategory.svg_icon) return null;
        return <CategoryIconSelected dangerouslySetInnerHTML={{ __html: currentCategory.svg_icon }} />;
    }, [filterCategoryId, memoizedCategories]);

    const arrangementIcon =
        flipComposition === flipXY ? (
            <RotateRight fontWeight={700} size={14} />
        ) : flipComposition === flipX ? (
            <MdOutlineFlip size={14} />
        ) : flipComposition === flipY ? (
            <FlipVertical size={14} />
        ) : (
            ''
        );

    const ButtonGroups = [
        {
            group: 1,
            options: [
                {
                    key: 'filter-button-wall',
                    className: 'filter-button sizeFilter',
                    dataCy: 'wall-size-button',
                    title: isCategoryStairs ? intl.formatMessage(
                        { id: 'studio.drawer.sizeFilter.width.title' },
                        {
                            width: `${calculateWallWidth(currentFrami.stairs_wall_shape) / 10} cm`,
                            strong: (...chunks) => <strong>{chunks}</strong>,
                        },
                    ) : intl.formatMessage(
                        { id: 'studio.drawer.sizeFilter.title' },
                        {
                            size: `${calculateWallWidth(currentFrami.wall_shape) / 10}x${calculateWallHeight(currentFrami.wall_shape) / 10
                                } cm`,
                            strong: (...chunks) => <strong>{chunks}</strong>,
                        }
                    ),
                    checked: true,
                    showCancelIcon: false,
                    onClick: () => {
                        if (!cookies.templateSizeClickHintSeen) {
                            setCookie('templateSizeClickHintSeen', true, { expires: 0, path: '/' });
                        }
                        openDrawer(WALLSIZE_DRAWER);
                    },
                    hasHint: true,
                },
                {
                    key: 'filter-button-arrangement',
                    className: 'filter-button',
                    dataCy: 'arrangement-filter-button',
                    checked: !!flipComposition,
                    title: <Nowrap>{intl.formatMessage({ id: 'studio.drawer.templates.filter.category' }, { icon: arrangementIcon })} {getCurrentCategoryIcon}</Nowrap>,
                    onCancel: () => setFilterCategoryId(null),
                    isMenu: true,
                    isOpen: isMenuOpen,
                    showCancelIcon: true,
                    checked: !!filterCategoryId,
                },
            ],
        },
        {
            group: 2,
            options: [
                {
                    key: 'filter-button-frame-count',
                    className: 'filter-button framesFilter',
                    dataCy: 'frame-count-filter-button',
                    title: frameFilterButtonTitle,
                    onClick: () => openDrawer(FRAME_COUNT_FILTER),
                    onCancel: () => setFilterFrameCounts([]),
                    showCancelIcon: true,
                    checked: !!notEmptyCount,
                },
                {
                    key: 'filter-button-price',
                    className: 'filter-button',
                    dataCy: 'price-filter-button',
                    title: intl.formatMessage(
                        { id: 'studio.drawer.templates.filter.price' },
                        {
                            colon: filterPriceApplied ? ':' : '',
                            range: filterPriceApplied ? `${filterPriceCurrency.min}-${filterPriceCurrency.max}` : '',
                            currency: filterPriceApplied ? currentFrami.currency : '',
                            strong: (...chunks) => <strong>{chunks}</strong>,
                        }
                    ),
                    onClick: () => openDrawer(PRICE_FILTER),
                    onCancel: () => setFilterPriceApplied(false),
                    showCancelIcon: true,
                    checked: !!filterPriceApplied,
                },
                {
                    key: 'filter-button-sorting',
                    className: 'filter-button',
                    dataCy: 'sorting-options-button',
                    title: intl.formatMessage({ id: 'studio.drawer.templates.filter.sorting' }),
                    isMenu: true,
                    options: sortOptions,
                    onCancel: () => setSortingCriterion("frameAreaDescending"),
                    checked: sortingCriterion !== "frameAreaDescending",
                    showCancelIcon: true,
                },
            ],
        },
    ];

    return (
        <FilterWrapper $isMobile={isMobile} $showFilters={showFilters}>
            {ButtonGroups.map((group) => (
                <ScrollHorizontalContainer key={group.group} padding={'0px'}>
                <ButtonGroup $row $isSmall={true} $wrap={!isMobile &&!smallHeightDevice} >
                    {group.options.map((option) => {
                        const { key, ...otherProps } = option;
                        if (option.isMenu) {
                            return option.options ? (
                                <FramiMenu key={option.key} options={option.options}>
                                    {(openMenu) => (
                                        <AccordionButton key={key} {...otherProps} onClick={openMenu} />
                                    )}
                                </FramiMenu>
                            ) : (
                                <FramiIconMenu key={option.key} onClose={handleCloseMenu} options={flipOptionsCategories}>
                                    {(openMenu) => (
                                        <AccordionButton key={key} {...otherProps} onClick={(ev) => handleOpenMenu(ev, openMenu)} />
                                    )}
                                </FramiIconMenu>
                            );
                        } else if (group.group === 1 && option.hasHint) {
                            return (
                                <RelativeWrapper key={option.key}>
                                    <AccordionButton key={key} {...otherProps} />
                                    {activeHint === 1.2 && <ToolbarAnimatedHint />}
                                </RelativeWrapper>
                            );
                        } else {
                            return <AccordionButton key={option.key} {...otherProps}/>;
                        }
                    })}
                </ButtonGroup>
                </ScrollHorizontalContainer>
            ))}
        </FilterWrapper>
    );
};

export default AdditionalFilters;
